import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Tooltip,
  Empty,
  Spin,
  Pagination,
  Divider
} from 'antd';
import {
  HistoryOutlined,
  EyeOutlined,
  PrinterOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import PatientSummaryComponent from '../Screens/PatientDetails/Summrize';
import ReportViewModal from '../Modals/ReportViewModal';
import { printReportService } from '../../utils/PrintReport';

const ReportHistoryTab = ({ 
  patient, 
  reportHistory, 
  pagination, 
  loading, 
  onPageChange 
}) => {
  const [viewReportModal, setViewReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewReportModal(true);
  };

  const handlePrintReport = (report) => {
    printReportService.printReport(report, patient);
  };

  const handlePageChange = (page, pageSize) => {
    onPageChange(page, pageSize);
  };

  const historyColumns = [
    {
      title: 'Generated Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <div>
          <div className="font-medium text-[#298d9f]">
            {dayjs(date).format('MMM DD, YYYY')}
          </div>
          <div className="text-gray-500 text-xs">
            {dayjs(date).format('HH:mm')}
          </div>
        </div>
      ),
      width: '20%',
    },
    {
      title: 'Report Preview',
      dataIndex: 'summaryHtml',
      key: 'summaryHtml',
      render: (summaryHtml) => {
        if (!summaryHtml) return 'No preview available';

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = summaryHtml;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        return (
          <div className="max-w-md">
            <p className="text-gray-600 line-clamp-3 text-sm">
              {plainText.substring(0, 150) + (plainText.length > 150 ? '...' : '')}
            </p>
          </div>
        );
      },
      width: '50%',
    },
    {
      title: 'Status',
      key: 'status',
      render: () => (
        <Tag color="success" className="rounded-full">
          <CheckCircleOutlined className="mr-1" />
          Complete
        </Tag>
      ),
      width: '15%',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Report">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewReport(record)}
              className="text-[#298d9f] hover:bg-[#298d9f]/10"
            />
          </Tooltip>
          <Tooltip title="Print Report">
            <Button
              type="text"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintReport(record)}
              className="text-gray-600 hover:bg-gray-50"
            />
          </Tooltip>
        </Space>
      ),
      width: '15%',
    },
  ];
  console.log('====================================');
  console.log(patient);
  // console.log(r);
  console.log('====================================');

  return (
    <>
      <Card
        title={
          <div className="flex justify-between items-center">
            <span className="text-primary font-medium">
              <HistoryOutlined className="mr-2" />
              Report History
            </span>
            <div className="text-sm text-gray-500">
              Total: {pagination.total} reports
            </div>
          </div>
        }
        bordered={false}
        className="bg-[#f9fafa]"
      >
        {patient.reportId&&
        <div className="mb-4">
          <PatientSummaryComponent patient={patient} />
        </div>}
        
        <Divider />

        <Spin spinning={loading}>
          {reportHistory.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="text-center">
                  <p className="text-gray-500">No report history found</p>
                  <p className="text-sm text-gray-400">
                    Generate your first AI medical report to see it here
                  </p>
                </div>
              }
            />
          ) : (
            <div>
              <Table
                columns={historyColumns}
                dataSource={reportHistory}
                rowKey={(record, index) => record.id || record.key || index}
                pagination={false}
                size="middle"
                className="custom-table"
                rowClassName="hover:bg-[#f0fdfa] transition-colors"
              />

              {pagination.total > 0 && (
                <div className="mt-6 text-center">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={true}
                    showQuickJumper={true}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} reports`
                    }
                    pageSizeOptions={['5', '10', '20', '50']}
                  />
                </div>
              )}
            </div>
          )}
        </Spin>
      </Card>

      <ReportViewModal
        visible={viewReportModal}
        onClose={() => {
          setViewReportModal(false);
          setSelectedReport(null);
        }}
        report={selectedReport}
        patient={patient}
      />
    </>
  );
};

ReportHistoryTab.propTypes = {
  patient: PropTypes.object.isRequired,
  reportHistory: PropTypes.array.isRequired,
  pagination: PropTypes.shape({
    current: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired
};

ReportHistoryTab.defaultProps = {
  loading: false
};

export default ReportHistoryTab;